import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { RealModeToggle } from '@/components/logistics/RealModeToggle';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Pause, Play, Plus, Square, Pencil } from 'lucide-react';
import { PromoForm } from '@/components/shipping-promos/PromoForm';
import { PromoSimulator } from '@/components/shipping-promos/PromoSimulator';

const fmt = (n) => (n == null || n === '' ? '—' : `$${Number(n).toLocaleString('es-CO')}`);
const STATUS = { draft: 'Borrador', active: 'Activa (simulación)', paused: 'Pausada', finished: 'Finalizada' };
const KIND = { free_over: 'Envío gratis desde monto', percent: 'Descuento %', fixed: 'Descuento fijo' };
const SUB_STATUS = { reserved: 'Reservado (pago pendiente)', consumed: 'Consumido (pago confirmado)', released: 'Liberado', reversed: 'Revertido' };
const dt = (iso) => (iso ? new Date(iso).toLocaleString('es-CO', { timeZone: 'America/Bogota' }) : '—');

const Card = ({ title, children, testid }) => (
  <section className="mt-6 border border-slate-200 bg-white p-5" data-testid={testid}>
    <h2 className="font-display text-lg font-bold text-slate-900">{title}</h2>
    <div className="mt-3">{children}</div>
  </section>
);

const PromoRow = ({ p, onEdit, onStatus }) => (
  <tr className="border-t border-slate-100 align-top" data-testid={`promo-row-${p.promo_id}`}>
    <td className="py-2 pr-3"><div className="font-semibold">{p.name}</div><div className="text-slate-400">{KIND[p.kind]}{p.value ? ` · ${p.kind === 'percent' ? `${p.value}%` : fmt(p.value)}` : ''}</div></td>
    <td className="pr-3" data-testid={`promo-status-${p.promo_id}`}>{STATUS[p.status]}</td>
    <td className="pr-3">{dt(p.starts_at)}<br />{dt(p.ends_at)}</td>
    <td className="pr-3">AM {p.automarket_pct}% · Vend. {100 - p.automarket_pct}%<br /><span className="text-slate-400">{p.stats.accepted_sellers} vendedor(es) aceptaron</span></td>
    <td className="pr-3">{fmt(p.stats.discount)} / {fmt(p.campaign_budget)}<br /><span className="text-slate-400">Mes: {fmt(p.stats.month_discount)} / {fmt(p.monthly_budget)} · Reservado {fmt(p.stats.reserved)}</span></td>
    <td className="pr-3">{p.stats.uses}</td>
    <td className="whitespace-nowrap">
      {['draft', 'paused'].includes(p.status) && <Button size="sm" variant="ghost" onClick={() => onEdit(p)} data-testid={`promo-edit-${p.promo_id}`}><Pencil className="h-4 w-4" /></Button>}
      {['draft', 'paused'].includes(p.status) && <Button size="sm" variant="ghost" onClick={() => onStatus(p, 'active')} data-testid={`promo-activate-${p.promo_id}`}><Play className="h-4 w-4" /></Button>}
      {p.status === 'active' && <Button size="sm" variant="ghost" onClick={() => onStatus(p, 'paused')} data-testid={`promo-pause-${p.promo_id}`}><Pause className="h-4 w-4" /></Button>}
      {p.status !== 'finished' && <Button size="sm" variant="ghost" onClick={() => onStatus(p, 'finished')} data-testid={`promo-finish-${p.promo_id}`}><Square className="h-4 w-4" /></Button>}
    </td>
  </tr>
);

const SubsidyTable = ({ data }) => (
  <div className="overflow-x-auto" data-testid="subsidy-ledger">
    <p className="mb-2 text-xs text-slate-500" data-testid="subsidy-totals">
      Consumido (pagos confirmados): {data.totals.count} · Descuento {fmt(data.totals.discount)} · AutoMarket {fmt(data.totals.subsidy_automarket)} · Vendedores {fmt(data.totals.subsidy_seller)}
      {Object.entries(data.by_status || {}).filter(([k]) => k !== 'consumed').map(([k, v]) => ` · ${SUB_STATUS[k] || k}: ${v.count} (${fmt(v.discount)})`).join('')}
    </p>
    <table className="w-full text-xs">
      <thead className="text-left text-slate-500"><tr><th className="py-1 pr-3">Pedido</th><th className="pr-3">Promoción</th><th className="pr-3">Envío cobrado</th><th className="pr-3">Descuento</th><th className="pr-3">AutoMarket</th><th className="pr-3">Vendedor</th><th className="pr-3">Estado</th><th>Fecha</th></tr></thead>
      <tbody>
        {data.items.map((x) => (
          <tr key={x.order_id} className="border-t border-slate-100" data-testid={`subsidy-row-${x.order_id}`}>
            <td className="py-1 pr-3 font-mono">{x.order_id}</td><td className="pr-3">{x.promo_name}</td><td className="pr-3">{fmt(x.base_shipping)}</td>
            <td className="pr-3">{fmt(x.discount)}</td><td className="pr-3">{fmt(x.subsidy_automarket)}</td><td className="pr-3">{fmt(x.subsidy_seller)}</td>
            <td className="pr-3" data-testid={`subsidy-status-${x.order_id}`}>{SUB_STATUS[x.status] || x.status}</td><td>{(x.created_at || '').slice(0, 10)}</td>
          </tr>
        ))}
        {!data.items.length && <tr><td colSpan={8} className="py-3 text-slate-400">Sin registros.</td></tr>}
      </tbody>
    </table>
  </div>
);

const Reconciliation = () => {
  const [r, setR] = useState(null);
  const run = async (apply) => {
    try { const { data } = apply ? await api.post('/admin/shipping-subsidies/reconciliation') : await api.get('/admin/shipping-subsidies/reconciliation'); setR(data); }
    catch { toast.error('No se pudo conciliar'); }
  };
  return (
    <div data-testid="subsidy-reconciliation">
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="rounded-none" onClick={() => run(false)} data-testid="reconcile-check-btn">Verificar</Button>
        <Button size="sm" variant="outline" className="rounded-none" onClick={() => run(true)} data-testid="reconcile-apply-btn">Corregir estados</Button>
      </div>
      {r && (
        <p className="mt-2 text-xs text-slate-600" data-testid="reconcile-result">
          Revisados {r.checked} · Inconsistencias {r.anomalies.length} · Corregidos {r.fixed.length}
          {r.anomalies.slice(0, 5).map((a) => <span key={a.order_id} className="block font-mono text-rose-700">{a.order_id}: {a.issues.join(', ')}</span>)}
        </p>
      )}
      <p className="mt-1 text-[11px] text-slate-400">Compara cada subsidio con su pedido y con el desembolso real. En simulación el desembolso no cambia; se muestra el proyectado.</p>
    </div>
  );
};

export default function AdminShippingPromotions() {
  const [items, setItems] = useState([]);
  const [budget, setBudget] = useState('');
  const [ttl, setTtl] = useState('');
  const [checkoutOn, setCheckoutOn] = useState(false);
  const [subs, setSubs] = useState({ items: [], totals: { count: 0 } });
  const [form, setForm] = useState({ open: false, initial: null });

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: s }] = await Promise.all([api.get('/admin/shipping-promotions'), api.get('/admin/shipping-subsidies')]);
      setItems(data.items || []);
      setBudget(data.settings?.monthly_budget_automarket ?? '');
      setTtl(data.settings?.reservation_ttl_minutes ?? '');
      setSubs(s);
    } catch { toast.error('No se pudieron cargar las promociones'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (p, status) => {
    if (!window.confirm(`¿Cambiar «${p.name}» a «${STATUS[status]}»?`)) return;
    try { await api.post(`/admin/shipping-promotions/${p.promo_id}/status`, { status }); toast.success('Estado actualizado'); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.message || 'No se pudo cambiar el estado'); }
  };
  const saveBudget = async () => {
    try { await api.put('/admin/shipping-promotions-settings', { monthly_budget_automarket: budget === '' ? null : Number(budget), reservation_ttl_minutes: ttl === '' ? null : Number(ttl), checkout_enabled: checkoutOn }); toast.success('Presupuesto guardado'); }
    catch (e) { toast.error(e.response?.data?.detail?.message || 'Revisa el valor'); }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-10 pb-24 sm:px-6 lg:px-8" data-testid="admin-shipping-promos-page">
        <Link to="/admin/configuracion/envios" className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Envíos y logística</Link>
        <h1 className="mt-1 font-display text-3xl font-black tracking-tighter text-slate-900">Promociones y subsidios de envío</h1>
        <RealModeToggle getUrl="/admin/shipping-promotions-settings" field="checkout_enabled" label="Descuentos reales en el pago"
          testid="promos-locked-banner" onChange={setCheckoutOn}
          onDesc="Descuentos reales activos: las promociones vigentes rebajan el envío que paga el comprador. AutoMarket y el vendedor fondean su parte; la comisión no cambia."
          offDesc="Modo simulación: las promociones activas solo se registran como «lo que se habría descontado». Requiere activar «Envío gratis o subsidiado» en Envíos y logística."
          save={(on) => api.put('/admin/shipping-promotions-settings', { monthly_budget_automarket: budget === '' ? null : Number(budget), reservation_ttl_minutes: ttl === '' ? null : Number(ttl), checkout_enabled: on })} />

        <Card title="Presupuesto mensual global de AutoMarket" testid="promos-global-budget">
          <div className="flex flex-wrap items-center gap-2">
            <Input type="number" min="0" placeholder="Sin límite global" className="w-56 rounded-none" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="promos-global-budget-input" />
            <Input type="number" min="5" placeholder="Minutos de reserva" className="w-44 rounded-none" value={ttl} onChange={(e) => setTtl(e.target.value)} data-testid="promos-reservation-ttl-input" />
            <Button className="rounded-none bg-slate-900" onClick={saveBudget} data-testid="promos-global-budget-save">Guardar</Button>
          </div>
          <p className="mt-1 text-xs text-slate-500">Suma máxima de subsidios asumidos por AutoMarket en el mes (hora Colombia), entre todas las campañas. El presupuesto solo se consume cuando Wompi confirma el pago; mientras tanto queda reservado durante los minutos indicados.</p>
        </Card>

        <Card title="Campañas" testid="promos-list">
          <Button size="sm" className="mb-3 rounded-none bg-orange-600 hover:bg-orange-700" onClick={() => setForm({ open: true, initial: null })} data-testid="promo-create-btn">
            <Plus className="mr-1 h-4 w-4" /> Nueva promoción
          </Button>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-slate-500"><tr><th className="py-1 pr-3">Promoción</th><th className="pr-3">Estado</th><th className="pr-3">Vigencia</th><th className="pr-3">Financiación</th><th className="pr-3">Consumo / presupuesto</th><th className="pr-3">Usos</th><th /></tr></thead>
              <tbody>
                {items.map((p) => <PromoRow key={p.promo_id} p={p} onEdit={(x) => setForm({ open: true, initial: x })} onStatus={changeStatus} />)}
                {!items.length && <tr><td colSpan={7} className="py-3 text-slate-400" data-testid="promos-empty">No hay promociones. Todas se crean en borrador.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Simulador de promociones" testid="promos-simulator-card"><PromoSimulator /></Card>
        <Card title="Registro de subsidios (independiente de comisiones)" testid="promos-subsidies-card"><SubsidyTable data={subs} /></Card>
        <Card title="Conciliación de subsidios y desembolsos" testid="promos-reconciliation-card"><Reconciliation /></Card>
      </div>
      <PromoForm open={form.open} initial={form.initial} onClose={() => setForm({ open: false, initial: null })}
        onSaved={() => { setForm({ open: false, initial: null }); load(); }} />
    </Layout>
  );
}
